import { useState } from "react"
import {
  useInspirationMapViewport,
  useInspirationPdf,
} from "@/hooks/use-inspiration-pdf"
import { useWebShareSupport } from "@/hooks/use-web-share-support"
import { Entity } from "@/types/entity"
import { Inspiration } from "@/types/inspiration"
import { Honeybadger } from "@honeybadger-io/react"
import copy from "copy-to-clipboard"

import { sendAnalyticsEvent } from "@/lib/analytics/amplitude-utils"
import { buildClientAxios } from "@/lib/client-axios"
import {
  buildAmplitudeEvent,
  entityIsEventActor,
  entityIsGuide,
  entityIsPlace,
  isHotel,
} from "@/lib/entity-utils"
import { getInspirationUrl } from "@/lib/inspiration"
import { pdfMapViewportQuery } from "@/lib/inspiration-api"
import { useRouter } from "@/lib/router"
import { canAddRefId, computeShareUrl } from "@/lib/share-utils"
import { selectOrganization } from "@/lib/storage/app-slice"
import { useAppSelector } from "@/lib/storage/hooks"
import {
  selectPlaceEndDate,
  selectPlaceStartDate,
  selectPlaceTravelers,
} from "@/lib/storage/place-slice"
import {
  selectCanPublishVanityLinkPermission,
  selectIsMindtripUser,
  selectUser,
  selectUserIsAnonymous,
} from "@/lib/storage/user-slice"
import { toastManager } from "@/lib/toast"
import { RETOOL_PLACES_URL } from "@/lib/utils/constants"
import { isTouch } from "@/lib/utils/dom"
import { shareUrlWithFallback } from "@/lib/web-share"
import IconBug from "@/components/icons/bug"
import IconDownload from "@/components/icons/download"
import IconEye from "@/components/icons/eye"
import IconInfo from "@/components/icons/info"
import IconLink from "@/components/icons/link"
import IconShare from "@/components/icons/share"
import { RegisterGate } from "./register-gate"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu"
import { IconButton, IconButtonProps } from "./ui/icon-button"
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover"

interface ShareEntityProps extends Omit<
  IconButtonProps,
  "icon" | "tooltip" | "label"
> {
  entity: Entity
  iconClassName?: string
}

export const shareEntityBanner = () => {
  return (
    <div className="mb-2 px-2">
      <div className="flex items-center justify-between">
        <div className="text-lg font-semibold">Share</div>
        <Popover>
          <PopoverTrigger
            render={
              <IconButton
                label="Share trackable link"
                variant="ghost"
                size="xs"
                icon={{ icon: IconInfo, size: 18 }}
                className="-mt-1 -mr-1.5 text-muted hover:text-foreground"
              />
            }
          />
          <PopoverContent
            size="auto"
            align="center"
            side="bottom"
            className="w-52 bg-black-a-11 px-3 py-1.5 text-xs text-white"
          >
            Shared link earnings are tracked on the Links tab in your Creator
            Hub
          </PopoverContent>
        </Popover>
      </div>
      <div className="text-xs">Share trackable link</div>
    </div>
  )
}

export function ShareEntity({
  entity,
  variant,
  iconClassName,
  ...props
}: ShareEntityProps) {
  const router = useRouter()
  const canPublishVanityLink = useAppSelector(
    selectCanPublishVanityLinkPermission
  )
  const user = useAppSelector(selectUser)
  const userIsAnonymous = useAppSelector(selectUserIsAnonymous)

  const isWebShareSupported = useWebShareSupport()
  const [shareUrl, setShareUrl] = useState<string>(entity.canonical_url)
  const organization = useAppSelector(selectOrganization)

  const placeStart = useAppSelector(selectPlaceStartDate)
  const placeEnd = useAppSelector(selectPlaceEndDate)
  const placeTravelers = useAppSelector(selectPlaceTravelers)
  const hotelSearchParams =
    entityIsPlace(entity) && isHotel(entity)
      ? { start: placeStart, end: placeEnd, travelers: placeTravelers }
      : undefined

  const { downloadPdf, isGenerating, error } = useInspirationPdf()
  const isInspirationPage = entityIsGuide(entity)
  // Anonymous user can't receive the PDF-ready notification, show print page copy instead
  const printPageCopy =
    userIsAnonymous && entityIsGuide(entity) && entity.customer_name
      ? entity
      : null
  const getPdfMapViewport = useInspirationMapViewport(
    isInspirationPage ? entity.entity_ref : undefined
  )
  const newUrl = computeShareUrl({
    user,
    entity,
    organization,
    canPublishVanityLink,
    router,
    hotelSearchParams,
  })

  if (shareUrl !== newUrl) {
    setShareUrl(newUrl)
  }

  const handleShare = async () => {
    try {
      const result = await shareUrlWithFallback(shareUrl)
      if (result === "shared" || result === "copied") {
        sendAnalyticsEvent(
          buildAmplitudeEvent(
            entity,
            result === "shared" ? "native" : "clipboard"
          )
        )
      }
      if (result === "copied") {
        toastManager.add({ type: "success", description: "Link copied!" })
      } else if (result === "copy-failed") {
        toastManager.add({
          type: "error",
          description: "Failed to copy link",
        })
      }
    } catch (error) {
      Honeybadger.notify(error instanceof Error ? error : String(error), {
        context: {
          source: "web-share",
          action: "shareEntity",
          entityRef: entity.entity_ref,
        },
      })
      console.error("Error sharing content:", error)
    }
  }

  const handlePdfDownload = async () => {
    if (!isInspirationPage) return

    try {
      await downloadPdf(entity as Inspiration, getPdfMapViewport())
      if (error) {
        toastManager.add({
          type: "error",
          description: `Failed to generate PDF: ${error}`,
        })
      }
    } catch (err) {
      console.error("PDF download error:", err)
      toastManager.add({ type: "error", description: "Failed to download PDF" })
    }
  }

  const handlePdfPreview = async () => {
    if (!isInspirationPage) return

    try {
      const axios = buildClientAxios()
      const res = await axios.get<{ html: string }>(
        `/api/inspirations/${entity.entity_ref}/pdf/preview${pdfMapViewportQuery(getPdfMapViewport())}`
      )
      const blob = new Blob([res.data.html], { type: "text/html" })
      const url = URL.createObjectURL(blob)
      window.open(url, "_blank", "noopener,noreferrer")
      setTimeout(() => URL.revokeObjectURL(url), 60000)
    } catch {
      toastManager.add({
        type: "error",
        description: "Failed to generate preview",
      })
    }
  }

  const iconButtonProps: IconButtonProps = {
    ...props,
    variant,
    label: "Share",
    icon: { icon: IconShare, className: iconClassName },
  }

  const isMindtripUser = useAppSelector(selectIsMindtripUser)
  let entitySecretUrl: string | undefined = undefined
  if (!entityIsEventActor(entity)) {
    entitySecretUrl = `${RETOOL_PLACES_URL}?type=${entity.type === "activity" ? "activities" : entity.type + "s"}&search=${entity.entity_ref}`
  }

  return isWebShareSupported && isTouch() ? (
    <IconButton
      onClick={(e) => {
        e.stopPropagation()
        void handleShare()
      }}
      {...iconButtonProps}
    />
  ) : (
    <DropdownMenu>
      <DropdownMenuTrigger
        render={
          <IconButton
            {...iconButtonProps}
            onClick={(e) => e.stopPropagation()}
          />
        }
      />
      <DropdownMenuContent className="min-w-[12em]">
        {canAddRefId({ user, entity, canPublishVanityLink }) && (
          <>{shareEntityBanner()}</>
        )}
        <DropdownMenuItem
          onClick={() => {
            if (copy(shareUrl)) {
              toastManager.add({ type: "success", description: "Link copied!" })
            } else {
              toastManager.add({
                type: "error",
                description: "Failed to copy link",
              })
            }
            sendAnalyticsEvent(buildAmplitudeEvent(entity, "clipboard"))
          }}
          icon={IconLink}
        >
          Copy link
        </DropdownMenuItem>
        {printPageCopy && printPageCopy.status === "published" && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={() =>
                window.open(
                  `${getInspirationUrl(printPageCopy)}/pdf`,
                  "_blank",
                  "noopener,noreferrer"
                )
              }
              icon={IconDownload}
            >
              Download PDF
            </DropdownMenuItem>
          </>
        )}
        {isInspirationPage && !printPageCopy && (
          <RegisterGate type="download-inspiration-pdf">
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={() => void handlePdfPreview()}
              icon={IconEye}
            >
              Preview PDF
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => void handlePdfDownload()}
              disabled={isGenerating}
              icon={IconDownload}
            >
              Download PDF
            </DropdownMenuItem>
          </RegisterGate>
        )}
        {isMindtripUser && entitySecretUrl && (
          <DropdownMenuItem
            icon={IconBug}
            onClick={() =>
              window.open(entitySecretUrl, "_blank", "noopener,noreferrer")
            }
          >
            Debug
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
